import { Item, ItemGroupPreview, ItemsResponseDTO } from '@dto/architecture.dto';
import { AxiosError } from "axios";
import { useContext, useEffect, useState } from 'react';
import { Link } from "react-router-dom";
import { API_URL } from "src/data/Api";
import { isClassLPAR } from "src/data/Class";
import { Log } from "src/service/log.service";
import { postApi } from "src/util/apiCalls";
import { GLOB } from 'src/util/Glob';
import { AllItemsContext } from '../comp/AllItems';
import Items, { CustomFormCompProps } from '../comp/Items';
import ModalSelectItems from '../comp/ModalSelectItems';
import { AdditionalApiPropsContext, RuleContext } from "../XmRule";
import { EnableItemId, RuleGroup } from "../XmRuleList";
import AddButton from './AddButton';

const ItemsChildren = (props: CustomFormCompProps<EnableItemId[]>) => {
  const { allItems, isLoadingAll } = useContext(AllItemsContext);
  const { getValue } = useContext(RuleContext);

  useEffect(() => {
    if (props.value === undefined)
      props.onChange?.([]);
  }, []);

  function makeTreeObj(itemTree: EnableItemId | Item) {
    let disabled = false;
    if ('disabled' in itemTree)
      disabled = itemTree.disabled;

    let title: JSX.Element;
    allItems.some(item => {
      if (item.item_id === itemTree.item_id) {
        title = makeItemLabelRoot(item);
        return true;
      }

      return false;
    });

    return { title, key: itemTree.item_id, disabled };
  }

  return <Items<EnableItemId>
    {...props}
    isLoading={isLoadingAll}
    makeTreeObj={makeTreeObj}
    renderAddButton={onAdd => <AddButton onClick={onAdd} />}
    modalProps={{
      initialValues: { items: props.value },
      content: <ModalSelectItemsChildren parents={getValue<RuleGroup['parents']>('parents')} />
    }}
  />;
};

function ModalSelectItemsChildren({ parents }: { parents: RuleGroup['parents']; }) {
  const { cls, subsystem, hw_type, allItems, isLoadingAll } = useContext(AllItemsContext);
  const { linuxExclude } = useContext(RuleContext);
  const [searchedItems, setSearchedItems] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { items, additionalPayload } = useContext(AdditionalApiPropsContext);

  useEffect(() => {
    const controller = new AbortController();
    const params = items?.select ? { ...items.select } : {};  // additionalApiParm.items.select

    postApi<ItemsResponseDTO, ItemGroupPreview>(
      API_URL.ARCHITECTURE + '/preview',
      {
        class: cls,
        subsystem,
        hw_type: isClassLPAR(cls) ? hw_type : undefined,
        regex: [".*"],
        linuxExclude,
        parents: parents && {
          ...parents,
          regex: parents.regex?.filter(parent => !!parent.subsystem),
          item_ids: parents.items?.map(item => item.item_id),
          tags: parents.tags?.map(tag => ({ tag_id: tag.tag_id, subsystem: tag.subsystem }))
        },
        ...additionalPayload
      },
      { signal: controller.signal, params: { rootParent: true, ...params } },
    ).then(
      ({ data: { data } }) => setSearchedItems(data),
      (reason: AxiosError) => Log.error('Failed to retrieve items!', reason)
    ).finally(() => setIsLoading(false));

    return () => controller.abort();
  }, []);

  function makeItemOfList({ item_id, disabled }: EnableItemId) {
    let title: JSX.Element;
    allItems.some(item => {
      if (item.item_id === item_id) {
        title = makeItemLabelRoot(item);

        return true;
      }

      return false;
    });

    return { title, key: item_id, disabled };
  }

  return (
    <ModalSelectItems<EnableItemId, { items: EnableItemId[]; }>
      makeItemOfList={makeItemOfList}
      makeLabel={makeItemLabelRoot}
      isLoading={isLoading || isLoadingAll}
      treeItems={searchedItems}
      handleCheckItem={({ item_id }) => ({ item_id, disabled: false })}
    />
  );
}

export function makeItemLabelRoot({ label, root_parent, url }: Item, useLinks?: boolean): JSX.Element {
  return (
    <span>
      {useLinks ? <Link to={GLOB.menuService.getRouteLink(url)}>{label}</Link> : label}
      {root_parent && <span className="xm-item-root-parent">{`  (${root_parent.label})`}</span>}
    </span>
  );
}

export default ItemsChildren;
